import { Divider } from 'primereact/divider';
import { classNames } from 'primereact/utils';
import { useTranslation } from 'react-i18next';

import RestrictedAccessCard from '../../../../../../../../../../shared/components/restricted-access-card/RestrictedAccessCard';
import styles from './CalculationDetails.module.css';

const BlurredCalculationDetails = (): JSX.Element => {
  const { t } = useTranslation();

  return (
    <div className='flex w-full justify-content-center'>
      <div className='flex flex-column gap-4 align-items-center relative w-full md:w-10 lg:w-8 xl:w-7'>
        <div className='blurred bg-white px-5 py-4 border-round-2xl flex flex-column gap-4 w-full'>
          {/* Title */}
          <h1 className='text-xl md:text-2xl font-medium p-0 mb-2'>
            {t('calculationDetailsPage.title')}
          </h1>

          <div className='flex p-4 bg-gray-50 w-full border-round-2xl mb-2' />

          {/* PCF Section */}
          <div className='flex flex-column gap-2 mb-2'>
            <div className='flex p-2 bg-gray-100 w-2 border-round-2xl' />

            <div className='flex flex-row gap-4 w-full'>
              <div
                className={classNames(
                  'p-5 border-round-2xl flex w-full flex-column gap-2',
                  styles['card'],
                )}
              >
                <div className='flex p-2 bg-gray-100 w-full border-round-2xl' />

                <div className='flex p-2 bg-gray-50 w-5 border-round-2xl' />
              </div>

              <div
                className={classNames(
                  'p-5 border-round-2xl flex w-full flex-column gap-2',
                  styles['card'],
                )}
              >
                <div className='flex p-2 bg-gray-100 w-full border-round-2xl' />

                <div className='flex p-2 bg-gray-50 w-5 border-round-2xl' />
              </div>
            </div>
          </div>

          {/* Emissions Breakdown Section */}
          <div className='flex flex-column gap-2 mb-2'>
            <div className='flex p-2 bg-gray-100 w-3 border-round-2xl' />
            <div className='flex w-full border-round-xl overflow-hidden mb-2'>
              <div className={classNames('flex p-3  w-12', styles['green-color-box'])} />
              <div className={classNames('flex py-3 px-1  w-3', styles['orange-color-box'])} />
              <div className={classNames('flex py-3 px-1 w-2', styles['blue-color-box'])} />
              <div className={classNames('flex py-3 px-1 w-1', styles['purple-color-box'])} />
            </div>

            <div className='flex flex-column gap-3 mb-2'>
              <div className='flex flex-row gap-3'>
                <div
                  className={classNames(
                    'p-5 border-round-2xl flex flex-row gap-3 align-items-center w-6',
                    styles['card'],
                  )}
                >
                  {/* Color */}
                  <div
                    className={classNames(
                      'flex border-round-lg align-items-center',
                      styles['green-color-box'],
                      styles['emission-breakdown'],
                    )}
                  />

                  <div className='flex w-full flex-column gap-2'>
                    <div className='flex p-2 bg-gray-100 w-3 border-round-2xl' />

                    <div className='flex p-2 bg-gray-50 w-5 border-round-2xl' />
                  </div>
                </div>

                <div
                  className={classNames(
                    'p-5 border-round-2xl flex flex-row gap-3 align-items-center w-6',
                    styles['card'],
                  )}
                >
                  {/* Color */}
                  <div
                    className={classNames(
                      'flex border-round-lg align-items-center',
                      styles['orange-color-box'],
                      styles['emission-breakdown'],
                    )}
                  />

                  <div className='flex w-full flex-column gap-2'>
                    <div className='flex p-2 bg-gray-100 w-5 border-round-2xl' />

                    <div className='flex p-2 bg-gray-50 w-4 border-round-2xl' />
                  </div>
                </div>
              </div>

              <div className='flex flex-row gap-3'>
                <div
                  className={classNames(
                    'p-5 border-round-2xl flex flex-row gap-3 align-items-center w-6',
                    styles['card'],
                  )}
                >
                  {/* Color */}
                  <div
                    className={classNames(
                      'flex border-round-lg align-items-center',
                      styles['blue-color-box'],
                      styles['emission-breakdown'],
                    )}
                  />

                  <div className='flex w-full flex-column gap-2'>
                    <div className='flex p-2 bg-gray-100 w-9 border-round-2xl' />

                    <div className='flex p-2 bg-gray-50 w-2 border-round-2xl' />
                  </div>
                </div>

                <div
                  className={classNames(
                    'p-5 border-round-2xl flex flex-row gap-3 align-items-center w-6',
                    styles['card'],
                  )}
                >
                  {/* Color */}
                  <div
                    className={classNames(
                      'flex border-round-lg align-items-center',
                      styles['purple-color-box'],
                      styles['emission-breakdown'],
                    )}
                  />

                  <div className='flex w-full flex-column gap-2'>
                    <div className='flex p-2 bg-gray-100 w-8 border-round-2xl' />

                    <div className='flex p-2 bg-gray-50 w-5 border-round-2xl' />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* General Information Section */}
          <div
            className={classNames(
              'p-5 border-round-2xl flex w-full flex-column gap-5',
              styles['card'],
            )}
          >
            <div className='flex p-2 bg-gray-100 w-5 border-round-2xl' />

            <div className='flex flex-row gap-4 w-full justify-content-between'>
              <div className='flex flex-column gap-1 w-3'>
                <div className='flex p-2 bg-gray-100 w-full border-round-2xl' />

                <div className='flex p-2 bg-gray-50 w-8 border-round-2xl' />
              </div>
              <div className='flex flex-column gap-1 w-3'>
                <div className='flex p-2 bg-gray-100 w-full border-round-2xl' />

                <div className='flex p-2 bg-gray-50 w-8 border-round-2xl' />
              </div>
              <div className='flex flex-column gap-1 w-3'>
                <div className='flex p-2 bg-gray-100 w-full border-round-2xl' />

                <div className='flex p-2 bg-gray-50 w-8 border-round-2xl' />
              </div>
              <div className='flex flex-column gap-1 w-3'>
                <div className='flex p-2 bg-gray-100 w-full border-round-2xl' />

                <div className='flex p-2 bg-gray-50 w-8 border-round-2xl' />
              </div>
            </div>
          </div>

          {/* Electricity Section */}
          <div
            className={classNames(
              'p-4 border-round-2xl flex w-full flex-column gap-5',
              styles['card'],
            )}
          >
            <div className='flex p-2 bg-gray-100 w-5 border-round-2xl mb-2' />

            <div className='flex flex-row gap-4 w-full justify-content-between'>
              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-5 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-3 border-round-2xl' />
              </div>

              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-5 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-3 border-round-2xl' />
              </div>
            </div>

            <div className='flex flex-column gap-1 w-7'>
              <div className='flex p-2 bg-gray-100 w-5 border-round-2xl' />
              <div className='flex p-2 bg-gray-50 w-3 border-round-2xl' />
            </div>

            <div className='flex flex-column w-full gap-1'>
              <div className='flex p-2 bg-gray-100 w-4 border-round-2xl' />
              <div className='flex p-2 bg-gray-50 w-full border-round-2xl' />
              <div className='flex p-2 bg-gray-50 w-full border-round-2xl' />
              <div className='flex p-2 bg-gray-50 w-2 border-round-2xl' />
            </div>
          </div>

          {/* Injection Molding Section */}
          <div
            className={classNames(
              'p-4 border-round-2xl flex w-full flex-column gap-5',
              styles['card'],
            )}
          >
            <div className='flex p-2 bg-gray-100 w-5 border-round-2xl mb-2' />

            <div className='flex p-2 bg-gray-100 w-2 border-round-2xl' />

            <div className='flex flex-row gap-4 w-full justify-content-between'>
              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-5 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-3 border-round-2xl' />
              </div>

              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-5 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-3 border-round-2xl' />
              </div>
            </div>

            <div className='flex flex-column gap-1 w-7'>
              <div className='flex p-2 bg-gray-100 w-5 border-round-2xl' />
              <div className='flex p-2 bg-gray-50 w-3 border-round-2xl' />
            </div>

            <div className='flex flex-column gap-1 w-8'>
              <div className='flex p-2 bg-gray-100 w-3 border-round-2xl' />
              <div className='flex p-2 bg-gray-50 w-9 border-round-2xl' />
            </div>

            <div className='flex p-2 bg-gray-100 w-2 border-round-2xl mt-2' />

            <div className='flex flex-row gap-4 w-full justify-content-between'>
              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-3 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-7 border-round-2xl' />
              </div>

              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-2 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-6 border-round-2xl' />
              </div>
            </div>
            <div className='flex flex-row gap-4 w-full justify-content-between'>
              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-5 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-1 border-round-2xl' />
              </div>

              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-5 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-1 border-round-2xl' />
              </div>
            </div>
            <div className='flex flex-row gap-4 w-full justify-content-between'>
              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-5 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-8 border-round-2xl' />
              </div>

              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-3 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-9 border-round-2xl' />
              </div>
            </div>

            <div className='flex p-2 bg-gray-100 w-5 border-round-2xl mt-2' />

            <div className='flex p-2 bg-gray-100 w-3 border-round-2xl -mb-4' />
            <div className='flex flex-row gap-4 w-full justify-content-between'>
              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-9 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-3 border-round-2xl' />
              </div>

              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-6 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-1 border-round-2xl' />
              </div>
            </div>

            <div className='flex p-2 bg-gray-100 w-2 border-round-2xl -mb-4' />
            <div className='flex flex-row gap-4 w-full justify-content-between'>
              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-9 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-3 border-round-2xl' />
              </div>

              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-6 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-1 border-round-2xl' />
              </div>
            </div>

            <div className='flex p-2 bg-gray-100 w-4 border-round-2xl -mb-4' />
            <div className='flex flex-row gap-4 w-full justify-content-between'>
              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-9 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-3 border-round-2xl' />
              </div>

              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-6 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-1 border-round-2xl' />
              </div>
            </div>

            <Divider className='p-0 m-0' />

            <div className='flex flex-row w-full'>
              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-3 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-8 border-round-2xl' />
              </div>
            </div>

            <div className='flex p-2 bg-gray-100 w-5 border-round-2xl mt-2' />

            <div className='flex p-2 bg-gray-100 w-3 border-round-2xl -mb-4' />
            <div className='flex flex-row gap-4 w-full justify-content-between'>
              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-9 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-3 border-round-2xl' />
              </div>

              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-6 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-1 border-round-2xl' />
              </div>
            </div>

            <div className='flex p-2 bg-gray-100 w-2 border-round-2xl -mb-4' />
            <div className='flex flex-row gap-4 w-full justify-content-between'>
              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-9 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-3 border-round-2xl' />
              </div>

              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-6 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-9 border-round-2xl' />
              </div>
            </div>

            <div className='flex flex-row gap-4 w-full justify-content-between'>
              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-4 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-1 border-round-2xl' />
              </div>

              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-6 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-7 border-round-2xl' />
              </div>
            </div>

            <div className='flex flex-column w-full gap-1'>
              <div className='flex p-2 bg-gray-100 w-4 border-round-2xl' />
              <div className='flex p-2 bg-gray-50 w-full border-round-2xl' />
              <div className='flex p-2 bg-gray-50 w-full border-round-2xl' />
              <div className='flex p-2 bg-gray-50 w-2 border-round-2xl' />
            </div>

            <Divider className='p-0 m-0' />

            <div className='flex flex-row w-full'>
              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-3 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-8 border-round-2xl' />
              </div>
            </div>
          </div>

          {/* Transportation Section */}
          <div
            className={classNames(
              'p-4 border-round-2xl flex w-full flex-column gap-5',
              styles['card'],
            )}
          >
            <div className='flex p-2 bg-gray-100 w-5 border-round-2xl mb-2' />

            <div className='flex p-2 bg-gray-100 w-3 border-round-2xl -mb-4' />
            <div className='flex flex-row gap-4 w-full justify-content-between'>
              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-9 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-3 border-round-2xl' />
              </div>

              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-2 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-3 border-round-2xl' />
              </div>
            </div>

            <div className='flex flex-row w-full'>
              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-3 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-8 border-round-2xl' />
              </div>
            </div>

            <div className='flex flex-row w-full'>
              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-3 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-12 border-round-2xl' />
              </div>
            </div>

            <div className='flex flex-row gap-4 w-full justify-content-between'>
              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-9 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-4 border-round-2xl' />
              </div>

              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-6 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-1 border-round-2xl' />
              </div>
            </div>

            <div className='flex flex-row w-full mb-4'>
              <div className='flex flex-column gap-1 w-6'>
                <div className='flex p-2 bg-gray-100 w-3 border-round-2xl' />
                <div className='flex p-2 bg-gray-50 w-8 border-round-2xl' />
              </div>
            </div>
          </div>

          <div className='flex p-2 bg-gray-100 w-5 border-round-2xl -mb-2' />
          <div className='flex p-2 bg-gray-50 w-full border-round-2xl py-8' />
        </div>

        <RestrictedAccessCard text={t('calculationDetailsPage.restrictedAccessCardText')} />
      </div>
    </div>
  );
};

export default BlurredCalculationDetails;
